import { useState } from 'react';

import BottomSheet from '@/components/BottomSheet';
import { PrimaryButton } from '@/components/Buttons';
import { toast } from '@/stores/toast';
import { useTranslation } from '@/utils/i18n';
import { submitBugReport, type FeedbackTrigger } from '@/utils/sentry';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Where the report is sent from (Sentry tag `feedback_trigger`). */
  trigger: FeedbackTrigger;
  /** Crash report: the Sentry event id it is linked to. */
  associatedEventId?: string;
  title?: string;
  placeholder?: string;
}

/**
 * Bug report in a Racore bottom sheet, sent to Sentry. Replaces the Sentry
 * feedback widget, which stretched to full height on mobile. On failure the
 * sheet stays open with the message, so the user can retry.
 */
export default function BugReportSheet({
  open,
  onClose,
  trigger,
  associatedEventId,
  title,
  placeholder,
}: Props) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await submitBugReport({ message: message.trim(), trigger, associatedEventId });
      toast.success(t('feedback.success'));
      setMessage('');
      onClose();
    } catch {
      toast.error(t('feedback.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title ?? t('feedback.bug.title')}
      testid="sheet-bug-report"
    >
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={placeholder ?? t('feedback.bug.placeholder')}
        maxLength={2000}
        rows={4}
        data-testid="input-bug-report"
        className="tw:w-full tw:resize-none tw:rounded-xl tw:border tw:border-blue-2/40 tw:bg-dark-primary tw:px-4 tw:py-3 tw:text-base tw:text-white tw:placeholder:text-grey-4"
      />
      <PrimaryButton
        label={t('feedback.submit')}
        onClick={() => void send()}
        disabled={!message.trim() || sending}
        loading={sending}
        className="tw:mt-4"
        data-testid="btn-bug-report-submit"
      />
    </BottomSheet>
  );
}
